import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ShoppingCart,
  Gift,
  CheckCircle,
  Sparkle,
  InstagramLogo,
  TiktokLogo,
  FacebookLogo,
  Users,
  Heart,
  Eye,
  Shield,
  Warning,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { WarrantySection } from '../components/WarrantySection';
import { PaymentTransferPanel } from '../components/PaymentTransferPanel';
import { OrderCompletedScreen } from '../components/OrderCompletedScreen';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { trackEvent } from '../lib/metaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = {
  Instagram: InstagramLogo,
  TikTok: TiktokLogo,
  Facebook: FacebookLogo,
};

const SERVICE_TYPE_ICONS = {
  followers: Users,
  likes: Heart,
  views: Eye,
};

const PLATFORM_COLORS = {
  Instagram: 'from-pink-500 via-purple-500 to-orange-400',
  TikTok: 'from-cyan-400 to-pink-500',
  Facebook: 'from-blue-600 to-blue-400',
};

const formatARS = (value) => `$${value.toLocaleString('es-AR')}`;
const formatNumber = (n) => n.toLocaleString('es-AR');

// 🔥 Marketing: precio "original" simulado para mostrar rebaja (visual únicamente)
const FAKE_DISCOUNT_MULTIPLIER = 1.5;
const FAKE_DISCOUNT_PERCENT = 33;
const fakeOriginalPrice = (real) => {
  const raw = real * FAKE_DISCOUNT_MULTIPLIER;
  // Redondear a los 100 más cercanos para lucir realista
  return Math.round(raw / 100) * 100;
};

export const CheckoutPage = () => {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [targetUrl, setTargetUrl] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponInfo, setCouponInfo] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [isFirstPurchase, setIsFirstPurchase] = useState(false);
  const [warrantyDays, setWarrantyDays] = useState(3);
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);
  const [orderCreated, setOrderCreated] = useState(null);
  const [receiptUploaded, setReceiptUploaded] = useState(false);
  const navigate = useNavigate();

  // 💾 Persistencia del pedido en localStorage:
  // Si el usuario recarga la pestaña o cierra el navegador por accidente,
  // al volver a /checkout recupera el pedido pendiente y puede seguir pagando
  // sin tener que rehacerlo. Solo se conservan pedidos aún NO pagados.
  const PENDING_ORDER_KEY = 'am_pending_order';
  const PENDING_ORDER_TTL_MS = 12 * 60 * 60 * 1000; // 12 horas

  useEffect(() => {
    fetchServices();
    // Auto-apply coupon from welcome modal
    const pendingCoupon = sessionStorage.getItem('apply_coupon');
    if (pendingCoupon) {
      setCouponCode(pendingCoupon);
      sessionStorage.removeItem('apply_coupon');
    }
    // 🔄 Restaurar pedido pendiente si existe y no venció
    try {
      const raw = localStorage.getItem(PENDING_ORDER_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const age = Date.now() - (parsed.savedAt || 0);
      if (age > PENDING_ORDER_TTL_MS) {
        localStorage.removeItem(PENDING_ORDER_KEY);
        return;
      }
      if (parsed.order && parsed.order.id) {
        // Revalidamos con el backend: si el pedido ya está completado o
        // cancelado, lo limpiamos. Si sigue esperando pago, restauramos el flujo.
        axios
          .get(`${API_URL}/api/orders/${parsed.order.id}/public-status`)
          .then(({ data }) => {
            const stillPending = ['awaiting_payment', 'payment_review', 'pending'].includes(data.status);
            if (stillPending) {
              // Fusiono lo que había en localStorage con lo actualizado del backend
              setOrderCreated({ ...parsed.order, ...data });
            } else {
              localStorage.removeItem(PENDING_ORDER_KEY);
            }
          })
          .catch(() => {
            // Si el pedido ya no existe u otro error, seguimos con la data local
            setOrderCreated(parsed.order);
          });
      }
    } catch (e) {
      localStorage.removeItem(PENDING_ORDER_KEY);
    }
  }, []);

  // Cada vez que se crea o cambia el pedido, lo guardamos
  useEffect(() => {
    if (orderCreated && !receiptUploaded) {
      try {
        localStorage.setItem(
          PENDING_ORDER_KEY,
          JSON.stringify({ order: orderCreated, savedAt: Date.now() })
        );
      } catch (e) { /* quota exceeded, ignore */ }
    }
    if (receiptUploaded) {
      // Ya subió comprobante → limpiamos
      try { localStorage.removeItem(PENDING_ORDER_KEY); } catch (e) {}
    }
  }, [orderCreated, receiptUploaded]);

  useEffect(() => {
    if (!couponCode || couponCode.length < 3) {
      setCouponInfo(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/coupons/validate`, {
          params: { code: couponCode },
        });
        setCouponInfo(data);
      } catch {
        setCouponInfo({ valid: false });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [couponCode]);

  useEffect(() => {
    const email = user?.email || guestEmail;
    if (email && email.includes('@')) {
      checkFirstPurchase(email);
    } else {
      setIsFirstPurchase(false);
    }
  }, [guestEmail, user]);

  const fetchServices = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/services`);
      setServices(data);
    } catch (error) {
      toast.error('Error al cargar servicios');
    } finally {
      setLoadingServices(false);
    }
  };

  const checkFirstPurchase = async (email) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/orders/check-first-purchase`, {
        params: { email },
      });
      setIsFirstPurchase(data.is_first_purchase);
    } catch (error) {
      setIsFirstPurchase(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedService || !selectedPackage) {
      toast.error('Selecciona un servicio y un paquete');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        service_id: selectedService.id,
        package_quantity: selectedPackage.quantity,
        target_url: targetUrl,
        coupon_code: couponCode || undefined,
        warranty_days: warrantyDays,
      };

      if (!user) {
        payload.guest_email = guestEmail;
        payload.guest_name = guestName;
      }

      // 🎯 Meta Pixel: usuario inició el checkout (justo antes de crear el pedido)
      const totalArs = (selectedPackage.price_ars || 0) + getWarrantyCost();
      trackEvent('InitiateCheckout', {
        content_name: selectedService.name,
        content_category: selectedService.platform,
        content_ids: [selectedService.id],
        contents: [
          { id: selectedService.id, quantity: 1, item_price: totalArs },
        ],
        num_items: 1,
        value: totalArs,
        currency: 'ARS',
      });

      const { data } = await axios.post(`${API_URL}/api/orders`, payload, {
        withCredentials: true,
      });
      setOrderCreated(data);

      // 🎯 Meta Pixel: Lead — el usuario entregó su email/nombre en el checkout
      // (contacto que el negocio puede usar más adelante). Solo para guests
      // porque los usuarios registrados ya generaron CompleteRegistration.
      if (!user && guestEmail) {
        trackEvent('Lead', {
          content_name: selectedService.name,
          content_category: selectedService.platform,
          value: totalArs,
          currency: 'ARS',
        });
      }

      toast.success('¡Pedido creado exitosamente!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear el pedido';
      toast.error(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setLoading(false);
    }
  };

  const getDisplayPrice = () => selectedPackage?.price_ars || 0;
  const getDisplayQuantity = () => selectedPackage?.quantity || 0;

  const getWarrantyCost = () => {
    if (warrantyDays === 30) return 2000;
    if (warrantyDays === 60) return 5100;
    if (warrantyDays === 365) return 14500;
    return 0;
  };

  const getWarrantyLabel = () => {
    if (warrantyDays === 30) return '30 días';
    if (warrantyDays === 60) return '60 días';
    if (warrantyDays === 365) return '1 año';
    return '3 días';
  };

  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.platform]) acc[service.platform] = [];
    acc[service.platform].push(service);
    return acc;
  }, {});

  if (loadingServices) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Receipt uploaded -> full-screen confirmation with countdown
  if (orderCreated && receiptUploaded) {
    return (
      <ErrorBoundary>
        <OrderCompletedScreen order={orderCreated} onClose={() => navigate('/')} />
      </ErrorBoundary>
    );
  }

  // Success view
  if (orderCreated) {
    return (
      <div className="min-h-screen py-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-red-500/40">
              <Warning size={36} className="text-red-500" weight="fill" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-red-500" data-testid="order-success-title">
              ⚠️ Falta pagar tu pedido
            </h2>
            <p className="text-muted-foreground text-sm">
              Guardamos el pedido{' '}
              <span className="font-mono font-semibold">
                #{(orderCreated?.id || '').slice(0, 8)}
              </span>{' '}
              a tu nombre. Para <strong>activarlo</strong> transferí el monto y subí el comprobante
              abajo. Si cerrás la pestaña por accidente, al volver a esta página vas a poder
              continuar donde quedaste.
            </p>
          </div>

          <div className="bg-muted rounded-lg p-4 mb-6 text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Servicio:</span>
              <span className="font-medium">{orderCreated.service_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Cantidad:</span>
              <span className="font-medium">{formatNumber(orderCreated.quantity)}</span>
            </div>
            {orderCreated.is_first_purchase_bonus && orderCreated.delivered_quantity > orderCreated.quantity && (
              <div className="flex justify-between text-[#00E676]">
                <span className="font-medium flex items-center gap-1">
                  <Gift size={16} weight="duotone" /> Bono aplicado — Recibirás:
                </span>
                <span className="font-bold">{formatNumber(orderCreated.delivered_quantity)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-border">
              <span className="text-muted-foreground">Total a transferir:</span>
              <span className="font-bold text-lg text-primary">{formatARS(orderCreated.price)}</span>
            </div>
          </div>

          <ErrorBoundary>
            <PaymentTransferPanel
              order={orderCreated}
              onComplete={() => navigate('/')}
              onReceiptUploaded={() => setReceiptUploaded(true)}
            />
          </ErrorBoundary>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Vas a descartar el pedido pendiente y empezar uno nuevo. ¿Continuar?')) {
                  try { localStorage.removeItem(PENDING_ORDER_KEY); } catch (e) {}
                  setOrderCreated(null);
                }
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline"
              data-testid="discard-pending-order"
            >
              Descartar este pedido y empezar uno nuevo
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Volver
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="checkout-title">
            Realizar Pedido
          </h1>
          <p className="text-muted-foreground">
            {user ? 'Selecciona un servicio y un paquete' : 'Compra rápida — sin necesidad de cuenta'}
          </p>
        </div>

        {isFirstPurchase && (
          <Card
            className="p-4 mb-6 border-primary/30 bg-primary/5"
            data-testid="first-purchase-banner"
          >
            <div className="flex items-start gap-3">
              <Sparkle size={24} weight="fill" className="text-primary mt-0.5" />
              <div>
                <h3 className="font-semibold text-primary mb-1">¡Bienvenido a Agency Market!</h3>
                <p className="text-sm text-muted-foreground">
                  Aprovechá los <strong>precios rebajados</strong> disponibles por tiempo limitado en todos los paquetes.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Step 1: Choose service */}
        {!selectedService && (
          <div className="space-y-8">
            <h2 className="text-xl font-semibold mb-4">1. Elegí el servicio</h2>
            {Object.entries(groupedServices).map(([platform, platformServices]) => {
              const PlatformIcon = PLATFORM_ICONS[platform];
              return (
                <div key={platform}>
                  <div className="flex items-center gap-2 mb-4">
                    {PlatformIcon && <PlatformIcon size={28} weight="duotone" />}
                    <h3 className="text-lg font-semibold">{platform}</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {platformServices.map((service) => {
                      const TypeIcon = SERVICE_TYPE_ICONS[service.service_type] || Heart;
                      const colorClass = PLATFORM_COLORS[service.platform] || 'from-primary to-primary';
                      const minPrice = Math.min(...service.packages.map((p) => p.price_ars));
                      return (
                        <Card
                          key={service.id}
                          className="p-6 border-border hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                          onClick={() => {
                            setSelectedService(service);
                            // 🎯 Meta Pixel: ViewContent al elegir un servicio
                            trackEvent('ViewContent', {
                              content_name: service.name,
                              content_category: service.platform,
                              content_ids: [service.id],
                              content_type: 'product',
                              currency: 'ARS',
                              value: minPrice,
                            });
                          }}
                          data-testid={`service-card-${service.id}`}
                        >
                          <div className="flex items-start gap-4">
                            <div
                              className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center shrink-0`}
                            >
                              <TypeIcon size={24} weight="fill" className="text-white" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold mb-1">{service.name}</h4>
                              <p className="text-xs text-muted-foreground mb-3">
                                {service.description}
                              </p>
                              <div className="flex items-center gap-2 flex-wrap text-sm">
                                <span className="text-xs text-muted-foreground">Desde</span>
                                <span className="text-xs text-gray-400 line-through">
                                  {formatARS(fakeOriginalPrice(minPrice))}
                                </span>
                                <span className="font-bold text-primary">{formatARS(minPrice)}</span>
                                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/40">
                                  -{FAKE_DISCOUNT_PERCENT}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Step 2: Choose package + form */}
        {selectedService && (
          <Card className="p-6 sm:p-8 border-border">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-sm text-muted-foreground">{selectedService.platform}</p>
                <h2 className="text-xl font-semibold">{selectedService.name}</h2>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedService(null);
                  setSelectedPackage(null);
                }}
                data-testid="change-service-button"
              >
                Cambiar
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Guest Info */}
              {!user && (
                <div className="space-y-4 pb-4 border-b border-border">
                  <h3 className="font-semibold">Tus datos</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="guestName">Nombre</Label>
                      <Input
                        id="guestName"
                        type="text"
                        placeholder="Juan Pérez"
                        value={guestName}
                        onChange={(e) => setGuestName(e.target.value)}
                        required
                        data-testid="guest-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guestEmail">Email</Label>
                      <Input
                        id="guestEmail"
                        type="email"
                        placeholder="tu@email.com"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        required
                        data-testid="guest-email-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Package selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Elegí el paquete</Label>
                  <span className="text-xs text-muted-foreground">Precios en pesos argentinos (ARS)</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {selectedService.packages.map((pkg) => {
                    const isSelected = selectedPackage?.quantity === pkg.quantity;
                    return (
                      <button
                        type="button"
                        key={pkg.quantity}
                        onClick={() => {
                          setSelectedPackage(pkg);
                          // 🎯 Meta Pixel: AddToCart al elegir un paquete
                          trackEvent('AddToCart', {
                            content_name: selectedService.name,
                            content_category: selectedService.platform,
                            content_ids: [selectedService.id],
                            content_type: 'product',
                            contents: [
                              { id: selectedService.id, quantity: pkg.quantity, item_price: pkg.price_ars },
                            ],
                            currency: 'ARS',
                            value: pkg.price_ars,
                          });
                        }}
                        className={`p-4 rounded-xl border text-left transition-all duration-200 ${
                          isSelected
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                            : 'border-border hover:border-primary/40 hover:bg-muted'
                        }`}
                        data-testid={`package-${pkg.quantity}`}
                      >
                        <div className="text-lg font-bold">{formatNumber(pkg.quantity)}</div>
                        <div className="text-xs text-muted-foreground mb-2">
                          {selectedService.service_type === 'followers' && 'Seguidores'}
                          {selectedService.service_type === 'likes' && 'Likes'}
                          {selectedService.service_type === 'views' && 'Vistas'}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs text-gray-400 line-through">
                            {formatARS(fakeOriginalPrice(pkg.price_ars))}
                          </span>
                          <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/40">
                            -{FAKE_DISCOUNT_PERCENT}%
                          </span>
                        </div>
                        <div className="text-primary font-bold">{formatARS(pkg.price_ars)}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          ARS · ${(pkg.price_ars / pkg.quantity).toFixed(2)}/u
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="targetUrl">URL del perfil o publicación</Label>
                <Input
                  id="targetUrl"
                  type="url"
                  placeholder={
                    selectedService.platform === 'Instagram'
                      ? 'https://instagram.com/tu-perfil'
                      : selectedService.platform === 'TikTok'
                      ? 'https://tiktok.com/@tu-perfil'
                      : 'https://facebook.com/tu-perfil'
                  }
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  required
                  data-testid="target-url-input"
                />
                <div
                  className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 mt-2"
                  data-testid="public-profile-notice"
                >
                  <span className="text-yellow-500 text-base leading-none mt-0.5">⚠️</span>
                  <p className="text-xs leading-snug">
                    <strong>Importante:</strong> el perfil debe estar{' '}
                    <strong className="text-yellow-500">público</strong> hasta que se complete el
                    pedido. Una vez entregado, podés volver a ponerlo en privado sin problema.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="couponCode">Código de cupón (opcional)</Label>
                <Input
                  id="couponCode"
                  type="text"
                  placeholder="AGENCYMARKET"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  data-testid="coupon-input"
                  className={
                    couponInfo?.valid
                      ? 'border-[#00E676] ring-1 ring-[#00E676]/40'
                      : couponInfo && !couponInfo.valid
                      ? 'border-destructive'
                      : ''
                  }
                />
                {couponInfo?.valid && (
                  <div className="flex items-center gap-2 text-sm text-[#00E676] mt-1" data-testid="coupon-valid">
                    <CheckCircle size={16} weight="fill" />
                    <span>{couponInfo.description || 'Cupón aplicado'}</span>
                  </div>
                )}
                {couponInfo && !couponInfo.valid && couponCode.length >= 3 && (
                  <p className="text-xs text-destructive mt-1">Cupón no válido</p>
                )}
              </div>

              {/* Warranty selection */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-2">
                  <Shield size={18} weight="duotone" className="text-[#00E676]" />
                  <Label>Garantía de recarga</Label>
                </div>
                <WarrantySection selected={warrantyDays} onSelect={setWarrantyDays} showHeader={false} compact={true} />
                <p className="text-xs text-muted-foreground">
                  Cubre cualquier baja del pedido. Si tu pedido no llega, también podés solicitar reembolso completo.
                </p>
              </div>

              {selectedPackage && (
                <div className="p-6 bg-primary/5 rounded-lg border border-primary/20 space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Servicio:</span>
                    <span>{formatARS(getDisplayPrice())}</span>
                  </div>
                  {getWarrantyCost() > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">
                        Garantía {getWarrantyLabel()}:
                      </span>
                      <span>+ {formatARS(getWarrantyCost())}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-3 border-t border-primary/20">
                    <span className="text-lg font-medium">Total ARS:</span>
                    <span className="text-3xl font-bold text-primary">
                      {formatARS(getDisplayPrice() + getWarrantyCost())}
                    </span>
                  </div>
                  {(() => {
                    const couponMultiplier = couponInfo?.valid ? couponInfo.bonus_multiplier || 1 : 1;
                    const finalMultiplier = couponMultiplier;
                    const qty = getDisplayQuantity();
                    if (finalMultiplier > 1 && qty > 0) {
                      return (
                        <div className="flex justify-between items-center pt-3 border-t border-primary/20">
                          <span className="text-sm font-medium flex items-center gap-2">
                            <Gift size={18} weight="duotone" className="text-[#00E676]" />
                            Bono cupón (x{finalMultiplier}):
                          </span>
                          <span className="text-xl font-bold text-[#00E676]">
                            {formatNumber(qty * finalMultiplier)}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading || !selectedPackage}
                data-testid="submit-order-button"
              >
                {loading ? (
                  'Procesando...'
                ) : (
                  <>
                    <ShoppingCart size={20} className="mr-2" />
                    Confirmar Pedido
                  </>
                )}
              </Button>

              {!user && (
                <p className="text-xs text-center text-muted-foreground">
                  ¿Ya tenés cuenta?{' '}
                  <a href="/login" className="text-primary hover:underline">
                    Iniciá sesión
                  </a>{' '}
                  para ver tu historial
                </p>
              )}
            </form>
          </Card>
        )}
      </div>
    </div>
  );
};
